/**
 * Helpers for building credential reminder emails from the admin side.
 *
 * Used by:
 *   - CredentialAudit per-row "Remind" button
 *   - PersonnelDetail Docs tab per-row "Remind" button
 *
 * Both feed the result into <ComposeModal /> via initialTo/initialSubject/initialBody.
 */

function daysBetween(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

function statusLine(c) {
  const days = daysBetween(c.expiration_date);
  if (c.status === 'missing') {
    return `${c.type} — not on file yet`;
  }
  if (days === null) {
    return `${c.type} — expiration unknown`;
  }
  if (days < 0) {
    return `${c.type} — expired ${Math.abs(days)} day(s) ago on ${c.expiration_date}`;
  }
  if (days === 0) {
    return `${c.type} — expires today (${c.expiration_date})`;
  }
  return `${c.type} — expires in ${days} day(s) on ${c.expiration_date}`;
}

function urgencyLabel(creds) {
  const hasExpired = creds.some(c => c.status === 'expired');
  if (hasExpired) return 'URGENT: Credential Expired';
  const hasExpiring = creds.some(c => c.status === 'expiring');
  if (hasExpiring) return 'Action Needed: Credential Expiring';
  return 'Credential Reminder';
}

/**
 * Build the prefill payload for a single recipient and one OR many credentials.
 *
 * @param {Object} args
 * @param {string} args.recipientName     - first/last name of the personnel
 * @param {string} args.recipientEmail    - the email to put in To
 * @param {Array<{type:string, status:string, expiration_date:string}>} args.credentials
 * @returns {{ to:string, subject:string, body:string }}
 */
export function buildCredentialReminder({ recipientName, recipientEmail, credentials }) {
  const creds = credentials || [];
  const first = (recipientName || '').trim().split(/\s+/)[0] || 'Team';
  const subject = creds.length === 1
    ? `${urgencyLabel(creds)} — ${creds[0].type}`
    : `${urgencyLabel(creds)} — ${creds.length} items`;

  const lines = creds.map(c => `  • ${statusLine(c)}`).join('\n');
  const body = `Hi ${first},

This is a friendly reminder about the following credential${creds.length > 1 ? 's' : ''} on file:

${lines}

Please upload an updated copy as soon as possible. Reply to this email if you have any questions.

Thanks,
Elite Home Rehab Credentialing Team`;

  return {
    to: recipientEmail || '',
    subject,
    body,
  };
}
